import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary hover:scale-[1.02]",
        outline: "border-2 border-input bg-background hover:bg-muted",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted",
        link: "text-primary-text underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground shadow-primary hover:scale-[1.03] font-bold",
        "hero-outline":
          "border-2 border-primary-onDark bg-transparent text-primary-foreground hover:bg-primary hover:text-primary-foreground",
        campaign:
          "bg-[#2563eb] text-white hover:bg-[#1d4ed8] uppercase tracking-wide font-bold rounded-md shadow-none hover:scale-100",
        teal: "bg-[#16a34a] text-white hover:bg-[#15803d] uppercase tracking-wide font-bold rounded-md shadow-none hover:scale-100",
        magenta:
          "bg-[#c026d3] text-white hover:bg-[#a21caf] uppercase tracking-wide font-bold rounded-md shadow-none hover:scale-100",
        gold:
          "bg-[#c9a227] text-white hover:bg-[#a8881c] uppercase tracking-wide font-bold rounded-md shadow-none hover:scale-100",
        ybera:
          "bg-neutral-900 text-white hover:bg-black rounded-xl shadow-none hover:scale-100 font-bold",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-14 rounded-xl px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
